import * as backend from '@/api/backend';
import { checkResponseStatus } from '@/util/check';
import { normalizeCompanyOpeningHours } from '@/utils/scheduleForm';
import { getCompanyNameError, getCompanyInfoError } from '@/utils/companyFormValidation';
import {
  DELIVERY_PLATFORMS,
  normalizeCompanyDeliveryPlatformsForSave,
  syncDeliveryPlatformEnabledFlags,
} from '@/utils/deliveryPlatforms';

const createDefaultDaySchedule = () => ({
  active: false,
  info: '',
  name: '',
  times: [{ start: '09:00', end: '18:00' }],
});

const defaultCompany = {
  companyID: null,
  name: '',
  info: '',
  email: '',
  mobile: '',
  website: '',
  instagram: '',
  googleReviewUrl: '',
  deliveryLieferandoUrl: '',
  deliveryUbereatsUrl: '',
  deliveryWoltUrl: '',
  deliveryLieferandoEnabled: false,
  deliveryUbereatsEnabled: false,
  deliveryWoltEnabled: false,
  longitude: '',
  latitude: '',
  address: '',
  more: [],
  monday: createDefaultDaySchedule(),
  tuesday: createDefaultDaySchedule(),
  wednesday: createDefaultDaySchedule(),
  thursday: createDefaultDaySchedule(),
  friday: createDefaultDaySchedule(),
  saturday: createDefaultDaySchedule(),
  sunday: createDefaultDaySchedule(),
  subscription: '{"package_id":"premium", "next_payment":null, "start_date":null }',
  boost: '{}',
  verified: 0,
  deleted: 0,
  date: '2019-07-03T18:15:56Z',
  cashierUser: 'Cashier',
  categoryID: 1,
  priceRange: null,
  smokingAllowed: null,
  dresscodeRequired: null,
  dresscodeInfo: null,
  timestamp: '2019-10-08T09:53:43Z',
  isDebugCompany: false,
  debugPartnerPremium: false,
  inviteQuota: 100,
  friendsQuota: 100,
  friendsCommissionRate: null,
};

export default {
  state: {
    companies: [],
    loading: false,
    error: false,
    companyEdit: {
      ...defaultCompany,
    },
    defaultCompany,
  },
  mutations: {
    fetch(state) {
      state.loading = true;
    },
    errorCompanies(state) {
      state.companies = [];
      state.loading = false;
      state.error = true;
    },
    setCompanies(state, companies) {
      state.companies = companies;
      state.loading = false;
      state.error = false;
    },
    editCompany(state, company) {
      if (!company) {
        state.companyEdit = {
          ...defaultCompany,
          monday: createDefaultDaySchedule(),
          tuesday: createDefaultDaySchedule(),
          wednesday: createDefaultDaySchedule(),
          thursday: createDefaultDaySchedule(),
          friday: createDefaultDaySchedule(),
          saturday: createDefaultDaySchedule(),
          sunday: createDefaultDaySchedule(),
        };
        return;
      }

      const formattedCompany = normalizeCompanyOpeningHours({
        ...company,
        deleted: company.deleted === 1,
        isDebugCompany: Number(company.isDebugCompany) === 1,
        debugPartnerPremium: Number(company.debugPartnerPremium) === 1,
        excludedFromCommission: Number(company.excludedFromCommission) === 1,
        commissionOnEveryRedemption: Number(company.commissionOnEveryRedemption) === 1,
        smokingAllowed: company.smokingAllowed === null || company.smokingAllowed === undefined
          ? null
          : Boolean(company.smokingAllowed),
        dresscodeRequired: company.dresscodeRequired === null || company.dresscodeRequired === undefined
          ? null
          : Boolean(company.dresscodeRequired),
        dresscodeInfo: company.dresscodeInfo == null ? null : String(company.dresscodeInfo),
        priceRange: company.priceRange === null || company.priceRange === undefined
          ? null
          : Number(company.priceRange),
      });

      state.companyEdit = syncDeliveryPlatformEnabledFlags({
        ...JSON.parse(JSON.stringify(defaultCompany)),
        ...formattedCompany,
        monday: formattedCompany.monday || createDefaultDaySchedule(),
        tuesday: formattedCompany.tuesday || createDefaultDaySchedule(),
        wednesday: formattedCompany.wednesday || createDefaultDaySchedule(),
        thursday: formattedCompany.thursday || createDefaultDaySchedule(),
        friday: formattedCompany.friday || createDefaultDaySchedule(),
        saturday: formattedCompany.saturday || createDefaultDaySchedule(),
        sunday: formattedCompany.sunday || createDefaultDaySchedule(),
      });
    },
    updateCompany(state, company) {
      Object.keys(company).forEach((key) => {
        state.companyEdit[key] = company[key];
      });
    },
    patchCompanyInList(state, { companyID, patch }) {
      const index = state.companies.findIndex(
        (company) => Number(company.companyID) === Number(companyID),
      );
      if (index === -1) return;

      state.companies[index] = {
        ...state.companies[index],
        ...patch,
        companyID: Number(companyID),
      };
    },
  },
  actions: {
    async fetchCompanies({ commit }) {
      commit('fetch');
      try {
        const response = await backend.getCompanies();
        await checkResponseStatus(200, response);
        const json = response.data;
        commit('setCompanies', json);
      } catch (error) {
        console.error(error);
        commit('errorCompanies');
      }
    },
    async createCompany({ dispatch }, {
      firebaseEmail, company, skipWelcomeEmail, initialPassword, requirePasswordChange,
    }) {
      const nameError = getCompanyNameError(company?.name);
      if (nameError) {
        throw new Error(nameError);
      }

      const infoError = getCompanyInfoError(company?.info);
      if (infoError) {
        throw new Error(infoError);
      }

      if (company.website && typeof company.website === 'string' && company.website.trim() !== '') {
        let url = company.website.trim();
        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url}`;
        }
        company.website = url;
      }
      
      if (company.instagram && typeof company.instagram === 'string' && company.instagram.trim() !== '') {
        let url = company.instagram.trim();
        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url}`;
        }
        company.instagram = url;
      }

      let payload = { ...company };
      payload = normalizeCompanyDeliveryPlatformsForSave(payload);

      const bodyForBackend = {
        ...normalizeCompanyOpeningHours(payload),
        deleted: company.deleted ? 1 : 0,
        verified: company.verified ? 1 : 0,
        firebaseEmail,
      };

      if (skipWelcomeEmail !== undefined) bodyForBackend.skipWelcomeEmail = skipWelcomeEmail;
      if (initialPassword !== undefined) bodyForBackend.initialPassword = initialPassword;
      if (requirePasswordChange !== undefined) bodyForBackend.requirePasswordChange = requirePasswordChange;

      const response = await backend.postCompany(bodyForBackend);
      await checkResponseStatus(201, response);

      const { companyID } = response.data;
      await dispatch('fetchCompanies');
      return companyID;
    },
    async saveCompany({ commit }, company) {
      const nameError = getCompanyNameError(company?.name);
      if (nameError) {
        throw new Error(nameError);
      }

      const infoError = getCompanyInfoError(company?.info);
      if (infoError) {
        throw new Error(infoError);
      }

      if (company.website && typeof company.website === 'string' && company.website.trim() !== '') {
        let url = company.website.trim();
        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url}`;
        }
        company.website = url;
      }

      if (company.instagram && typeof company.instagram === 'string' && company.instagram.trim() !== '') {
        let url = company.instagram.trim();
        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url}`;
        }
        company.instagram = url;
      }

      let payload = { ...company };
      payload = normalizeCompanyDeliveryPlatformsForSave(payload);

      const formattedCompany = {
        ...normalizeCompanyOpeningHours(payload),
        deleted: company.deleted ? 1 : 0,
        isDebugCompany: company.isDebugCompany ? 1 : 0,
        debugPartnerPremium: company.debugPartnerPremium ? 1 : 0,
        excludedFromCommission: company.excludedFromCommission ? 1 : 0,
        commissionOnEveryRedemption: company.commissionOnEveryRedemption ? 1 : 0,
        smokingAllowed: company.smokingAllowed === true
          ? true
          : company.smokingAllowed === false
            ? false
            : null,
        dresscodeRequired: company.dresscodeRequired === true ? true : null,
        dresscodeInfo: company.dresscodeInfo?.trim() ? company.dresscodeInfo.trim() : null,
        priceRange: company.priceRange == null || company.priceRange === ''
          ? null
          : Number(company.priceRange),
      };

      const response = await backend.putCompany(formattedCompany.companyID, formattedCompany);
      await checkResponseStatus(204, response);

      const deliveryPatch = {};
      DELIVERY_PLATFORMS.forEach((platform) => {
        deliveryPatch[platform.apiField] = formattedCompany[platform.apiField] || '';
      });
      commit('updateCompany', syncDeliveryPlatformEnabledFlags({
        ...company,
        ...deliveryPatch,
      }));

      commit('patchCompanyInList', {
        companyID: formattedCompany.companyID,
        patch: formattedCompany,
      });
    },
    async verifyCompany(context, { companyID }) {
      const response = await backend.putCompany(companyID, { verified: 1 });
      await checkResponseStatus(204, response);
    },
    async unverifyCompany(context, { companyID }) {
      const response = await backend.putCompany(companyID, { verified: 0 });
      await checkResponseStatus(204, response);
    },
    async saveLoginEmail(context, { companyID, email }) {
      const response = await backend.postCompanyEmail(companyID, { email });
      await checkResponseStatus(200, response);
    },
    async getLoginEmailStatus(context, companyID) {
      const response = await backend.getCompanyLoginEmailStatus(companyID);
      await checkResponseStatus(200, response);
      return response.data;
    },
    async verifyLoginEmail(context, { companyID }) {
      const response = await backend.postCompanyVerifyLoginEmail(companyID);
      await checkResponseStatus(200, response);
      return response.data;
    },

    // eslint-disable-next-line no-empty-pattern
    async reviveCompany(context, { companyID }) {
      const response = await backend.putCompany(companyID, { deleted: 0 });
      await checkResponseStatus(204, response);
    },

    // eslint-disable-next-line no-empty-pattern
    async deleteCompany({ dispatch }, { companyID }) {
      const response = await backend.deleteCompany(companyID);
      if (response.status === 428) {
        const err = new Error('cancelled');
        err.code = 'step_up_cancelled';
        throw err;
      }
      await checkResponseStatus(204, response);
      await dispatch('fetchCompanies');
    },
    async getPasswordResetLink(context, companyID) {
      const response = await backend.getCompanyPasswordResetLink(companyID);
      await checkResponseStatus(200, response);
      return response.data;
    },
  },
  namespaced: true,
};
